import { useRef, useState } from "react";
import { motion } from "framer-motion";
import {
  Play,
  Star,
  ChevronLeft,
  ChevronRight,
  X,
  ArrowRight,
} from "lucide-react";
import { Link } from "react-router-dom";
import { REELS } from "@/lib/reels";

function ReelCard({ reel, onPlay, index }) {
  return (
    <motion.button
      type="button"
      initial={{ opacity: 0, y: 20 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, amount: 0.1 }}
      transition={{ duration: 0.5, delay: index * 0.05 }}
      onClick={() => onPlay(reel)}
      data-testid={`reel-card-${index}`}
      className="group relative shrink-0 w-[220px] sm:w-[240px] md:w-[260px] aspect-[9/16] rounded-3xl overflow-hidden border border-white/10 bg-[#0a0a0a] snap-start text-left"
    >
      <img
        src={reel.thumbnail}
        alt={reel.name}
        className="absolute inset-0 w-full h-full object-cover transition-transform duration-700 group-hover:scale-110"
        loading="lazy"
      />
      <div className="absolute inset-0 bg-gradient-to-t from-black via-black/50 to-black/20" />

      {/* Play overlay */}
      <div className="absolute inset-0 flex items-center justify-center">
        <div className="w-14 h-14 rounded-full bg-white/10 backdrop-blur-md border border-white/30 flex items-center justify-center group-hover:bg-gradient-to-br group-hover:from-blue-600 group-hover:to-cyan-400 group-hover:border-transparent group-hover:scale-110 transition-all duration-500">
          <Play className="w-5 h-5 text-white group-hover:text-black translate-x-0.5" fill="currentColor" />
        </div>
      </div>

      {/* Top rating */}
      <div className="absolute top-3 left-3 right-3 flex items-center justify-between">
        <div className="inline-flex items-center gap-1 px-2 py-1 rounded-full bg-black/50 backdrop-blur-md border border-white/10">
          {[...Array(reel.rating)].map((_, k) => (
            <Star key={k} className="w-2.5 h-2.5 fill-cyan-400 text-cyan-400" />
          ))}
        </div>
        <span className="text-[9px] uppercase tracking-widest text-white/80 font-semibold px-2 py-1 rounded-full bg-black/50 backdrop-blur-md border border-white/10">
          Reel
        </span>
      </div>

      {/* Bottom info */}
      <div className="absolute left-4 right-4 bottom-4">
        <p className="font-display text-base font-semibold text-white leading-snug line-clamp-2">
          &ldquo;{reel.quote}&rdquo;
        </p>
        <div className="mt-3 pt-3 border-t border-white/15">
          <div className="text-sm font-semibold text-white">{reel.name}</div>
          <div className="text-xs text-white/60">{reel.company}</div>
        </div>
      </div>
    </motion.button>
  );
}

function ReelModal({ reel, onClose }) {
  if (!reel) return null;
  return (
    <div
      className="fixed inset-0 z-[100] flex items-center justify-center p-4 bg-black/80 backdrop-blur-lg"
      onClick={onClose}
      data-testid="reel-modal"
    >
      <div
        onClick={(e) => e.stopPropagation()}
        className="relative w-full max-w-sm aspect-[9/16] rounded-3xl overflow-hidden border border-white/10 bg-[#0a0a0a]"
      >
        {reel.video ? (
          <video
            src={reel.video}
            controls
            autoPlay
            playsInline
            poster={reel.thumbnail}
            className="w-full h-full object-cover"
          />
        ) : (
          <>
            <img
              src={reel.thumbnail}
              alt={reel.name}
              className="absolute inset-0 w-full h-full object-cover"
            />
            <div className="absolute inset-0 bg-gradient-to-t from-black via-black/70 to-black/40" />
            <div className="absolute inset-0 flex flex-col items-center justify-center text-center px-6">
              <div className="inline-flex items-center gap-1 mb-4">
                {[...Array(reel.rating)].map((_, k) => (
                  <Star key={k} className="w-4 h-4 fill-cyan-400 text-cyan-400" />
                ))}
              </div>
              <p className="font-display text-2xl font-bold leading-snug">
                &ldquo;{reel.quote}&rdquo;
              </p>
              <div className="mt-6">
                <div className="font-semibold text-white">{reel.name}</div>
                <div className="text-sm text-white/60">
                  {reel.role} · {reel.company}
                </div>
              </div>
              <div className="mt-8 inline-flex items-center gap-2 text-xs text-white/50">
                <Play className="w-3 h-3 text-cyan-400" fill="currentColor" />
                Video reel coming soon
              </div>
            </div>
          </>
        )}

        <button
          onClick={onClose}
          data-testid="reel-modal-close"
          aria-label="Close"
          className="absolute top-3 right-3 w-9 h-9 rounded-full bg-black/60 backdrop-blur-md border border-white/15 flex items-center justify-center text-white hover:bg-black/80 transition-colors"
        >
          <X className="w-4 h-4" />
        </button>
      </div>
    </div>
  );
}

export default function ClientReels({ showViewAll = true }) {
  const scroller = useRef(null);
  const [active, setActive] = useState(null);

  const scroll = (dir) => {
    if (!scroller.current) return;
    scroller.current.scrollBy({
      left: dir * (scroller.current.clientWidth * 0.7),
      behavior: "smooth",
    });
  };

  return (
    <section
      data-testid="client-reels"
      className="section relative overflow-hidden"
    >
      <div className="max-w-7xl mx-auto px-6 md:px-12">
        <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-5 mb-10">
          <div>
            <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-cyan-500/10 border border-cyan-500/20 text-cyan-300 text-xs font-semibold tracking-widest uppercase">
              Client Reels
            </div>
            <h2 className="mt-5 font-display text-4xl md:text-5xl font-bold tracking-tight">
              Hear it straight from{" "}
              <span className="text-gradient-brand">our clients.</span>
            </h2>
          </div>

          <div className="flex items-center gap-3">
            {showViewAll && (
              <Link
                to="/reviews"
                data-testid="reels-view-all"
                className="hidden sm:inline-flex items-center gap-2 text-sm text-cyan-400 hover:text-cyan-300 font-semibold"
              >
                View all reviews <ArrowRight className="w-4 h-4" />
              </Link>
            )}
            <button
              data-testid="reels-prev"
              onClick={() => scroll(-1)}
              className="w-11 h-11 rounded-full bg-white/5 border border-white/10 flex items-center justify-center hover:bg-white/10 transition-colors"
              aria-label="Scroll left"
            >
              <ChevronLeft className="w-4 h-4" />
            </button>
            <button
              data-testid="reels-next"
              onClick={() => scroll(1)}
              className="w-11 h-11 rounded-full bg-gradient-to-r from-blue-600 to-cyan-400 text-black flex items-center justify-center hover:shadow-[0_0_20px_rgba(0,229,255,0.4)] transition-all"
              aria-label="Scroll right"
            >
              <ChevronRight className="w-4 h-4" />
            </button>
          </div>
        </div>
      </div>

      {/* Edge-bleed scroller */}
      <div className="relative">
        <div className="absolute inset-y-0 left-0 w-16 md:w-32 bg-gradient-to-r from-[#050505] to-transparent z-10 pointer-events-none" />
        <div className="absolute inset-y-0 right-0 w-16 md:w-32 bg-gradient-to-l from-[#050505] to-transparent z-10 pointer-events-none" />
        <div
          ref={scroller}
          data-testid="reels-scroller"
          className="flex gap-4 overflow-x-auto snap-x snap-mandatory scroll-smooth px-6 md:px-12 pb-4"
          style={{ scrollbarWidth: "none" }}
        >
          {REELS.map((r, i) => (
            <ReelCard key={r.id} reel={r} index={i} onPlay={setActive} />
          ))}
        </div>
      </div>

      <ReelModal reel={active} onClose={() => setActive(null)} />
    </section>
  );
}
